import React, { useCallback, useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { FilterLeaderByLanguage } from "../../store/actions/user.action";
import { base_url, human_languages, programing_languages } from "../../components/Transformation/Transformations";
import ActiveLingoCard from "../../components/Cards/ActiveLingoCard";
import Pagination from "../../components/Pagination/Pagination";
import Loader from "../../components/Loader/Loader";
import TimeBlockFilter from "../../components/Filter/TimeBlockFilter";
import { isLeaderAvailableInBlock } from "../../utils/availabilityFilter";

function NonLoginLanguageButtons({ 
  token,
  activeButton, 
  setActiveButton,
  setPage,
  page
}) {
  const { t } = useTranslation();
  const navigation = useNavigate();
  const dispatch = useDispatch();
  const [searchLanguage, setSearchLanguage] = useState("");
  const [loading, setLoading] = React.useState(false);
  const [fetchingAvailabilities, setFetchingAvailabilities] = useState(false);
  const [availabilitiesMap, setAvailabilitiesMap] = useState({});
  const [selectedTimeBlock, setSelectedTimeBlock] = useState("all");
  const allShortLeaders = useSelector((state) => state.user.allShortLeaders);
  const currentUser = useSelector((state) => state.user.currentUser);

  const fuhsdButtonId = human_languages.length + programing_languages.length + 1;

  const fetchLeaders = useCallback(
    async (currentPage, searchLanguage) => {
      setLoading(true);
      await dispatch(
        FilterLeaderByLanguage(token, searchLanguage, currentPage)
      );
      setLoading(false);
    },
    [token, dispatch]
  );

  useEffect(() => {
    if (searchLanguage || selectedTimeBlock !== "all") {
      fetchLeaders(page, searchLanguage);
    }
  }, [token, page, fetchLeaders, searchLanguage, selectedTimeBlock]);

  useEffect(() => {
    setPage(1);
  }, [searchLanguage, setPage]);

  useEffect(() => {
    if (!allShortLeaders?.results || selectedTimeBlock === "all") return;

    const leadersToFetch = allShortLeaders.results.filter(
      (leader) =>
        leader.id &&
        !availabilitiesMap[leader.id] &&
        !leader.owner?.availabilities &&
        !leader.availabilities
    );

    if (leadersToFetch.length === 0) return;

    let isMounted = true;
    setFetchingAvailabilities(true);

    Promise.all(
      leadersToFetch.map((leader) =>
        axios
          .get(`${base_url}/clubusers/leader-profiles/${leader.id}/`)
          .then((res) => ({
            id: leader.id,
            availabilities:
              res.data?.owner?.availabilities ||
              res.data?.availability_time ||
              [],
          }))
          .catch(() => ({ id: leader.id, availabilities: [] }))
      )
    ).then((fetchedResults) => {
      if (!isMounted) return;
      setAvailabilitiesMap((prev) => {
        const next = { ...prev };
        fetchedResults.forEach(({ id, availabilities }) => {
          next[id] = availabilities;
        });
        return next;
      });
      setFetchingAvailabilities(false);
    });

    return () => {
      isMounted = false;
    };
  }, [allShortLeaders, selectedTimeBlock, availabilitiesMap]);

  const filteredLeaders = useMemo(() => {
    if (!allShortLeaders?.results) return [];
    return allShortLeaders.results.filter((leader) =>
      isLeaderAvailableInBlock(
        leader,
        selectedTimeBlock,
        currentUser?.timezone,
        availabilitiesMap[leader.id]
      )
    );
  }, [allShortLeaders, selectedTimeBlock, currentUser?.timezone, availabilitiesMap]);

  const numberOfPages = Math.ceil((filteredLeaders?.length || 0) / 10) || 0;

  const handleBtn = (buttonId, language) => {
    if (activeButton === buttonId) {
      setActiveButton(null);
      setSearchLanguage("");
    } else {
      setActiveButton(buttonId);
      setSearchLanguage(language);
    }
    setPage(1);
  };

  const handleFUHSDClick = () => {
    if (!token) {
      toast.warn(t("generic.please_login"), {
        position: "bottom-right",
      });
      navigation("/login");
      return;
    }
    setActiveButton(fuhsdButtonId);
    setPage(1);
  };

  const showResults = Boolean(searchLanguage || selectedTimeBlock !== "all");

  return (
    <>
      <TimeBlockFilter
        selectedBlock={selectedTimeBlock}
        setSelectedBlock={setSelectedTimeBlock}
      />

      <div className="bg-white border border-[var(--border)] rounded-full p-2 flex flex-wrap gap-2 justify-center max-w-4xl mx-auto mt-6">
        {human_languages.map((lang, index) => (
          <button
            key={lang.value}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
              activeButton === index + 1
                ? 'bg-[var(--brand)] text-white'
                : 'bg-transparent text-[var(--muted)] hover:bg-[var(--brand)] hover:text-white'
            }`}
            onClick={() => handleBtn(index + 1, lang.value)}
          >
            {t("redesign.learn_prefix") + " " + t(lang.label)}
          </button>
        ))}
      </div>

      <div className="bg-white border border-[var(--border)] rounded-full p-2 flex flex-wrap gap-2 justify-center max-w-4xl mx-auto mt-4">
        {programing_languages.map((lang, index) => (
          <button
            key={lang.value}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
              activeButton === index + human_languages.length + 1
                ? 'bg-[var(--brand)] text-white'
                : 'bg-transparent text-[var(--muted)] hover:bg-[var(--brand)] hover:text-white'
            }`}
            onClick={() => handleBtn(index + human_languages.length + 1, lang.value)}
          >
            {t("redesign.learn_prefix") + " " + t(lang.label)}
          </button>
        ))}
        <button
          className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
            activeButton === fuhsdButtonId
              ? 'bg-[var(--brand)] text-white'
              : 'bg-transparent text-[var(--muted)] hover:bg-[var(--brand)] hover:text-white'
          }`}
          onClick={handleFUHSDClick}
        >
          FUHSD ELD Certified
        </button>
      </div>

      {showResults && (
        <>
          <div className="pb-[2rem] mt-6">
            {loading || fetchingAvailabilities ? (
              <Loader />
            ) : filteredLeaders && filteredLeaders.length > 0 ? (
              <div className="grid sm:grid-cols-1 lg:grid-cols-2 md:gap-10 gap-5 max-w-[1100px] mx-auto my-0 w-full mb-4">
                {filteredLeaders.map((leader) => (
                  <ActiveLingoCard
                    id={leader.id}
                    key={leader.id}
                    image={leader.owner.image_thumbnail}
                    name={leader.owner.first_name + " " + leader.owner.last_name}
                    teaching_language={leader.teaching_language}
                    reviewCount={leader?.owner?.review_count}
                    reviewScore={leader?.owner?.review_score}
                    credit_adjustment_rate={leader?.credit_adjustment_rate}
                    also_speak={leader?.owner?.also_speak}
                  />
                ))}
              </div>
            ) : (
              <p className="font-semibold text-xl text-center">
                {t("leader_page.no_leader_text")}
              </p>
            )}
          </div>
          <div className="flex justify-center">
            <Pagination pageNumber={numberOfPages} setPage={setPage} page={page} />
          </div>
        </>
      )}
    </>
  );
}

export default NonLoginLanguageButtons;


